'use client';

import clsx from 'clsx';
import { useContext, useId, type ChangeEvent } from 'react';
import Checkbox from '../checkbox/Checkbox';
import RadioButton from '../radio/RadioButton';
import { SelectionListContext } from './SelectionList';
import styles from './SelectionList.module.scss';

export type SelectionListBadgeColor = 'green' | 'red' | 'blue' | 'black' | 'primary' | 'purple' | 'yellow' | 'gray';

export interface SelectionListItemProps {
  value: string;
  label: string;
  subLabel?: string;
  /** 단일 뱃지 (색은 `badgeColor`) */
  badge?: string;
  badgeColor?: SelectionListBadgeColor;
  /** 여러 개의 회색 뱃지 (예: 상품 태그). 있으면 `label` 옆에 순서대로 표시 */
  tags?: string[];
  thumbnail?: string;
  /** `true`이면 `thumbnail`이 없을 때도 왼쪽에 썸네일 영역(플레이스홀더) 표시 */
  thumbnailPlaceholder?: boolean;
  disabled?: boolean;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

const BADGE_COLOR_CLASS: Record<SelectionListBadgeColor, string> = {
  primary: styles.badge_primary,
  purple: styles.badge_purple,
  yellow: styles.badge_yellow,
  green: styles.badge_green,
  red: styles.badge_red,
  blue: styles.badge_blue,
  black: styles.badge_black,
  gray: styles.badge_gray,
};

function emitCheckboxListChange(
  onChange: SelectionListItemProps['onChange'],
  name: string,
  value: string,
  checked: boolean,
) {
  if (!onChange) return;
  const target = { name, value, type: 'checkbox' as const, checked } as unknown as HTMLInputElement;
  onChange({ target, currentTarget: target } as unknown as ChangeEvent<HTMLInputElement>);
}

export default function SelectionListItem({
  value,
  label,
  subLabel,
  badge,
  badgeColor = 'gray',
  tags,
  thumbnail,
  thumbnailPlaceholder = false,
  disabled = false,
  checked,
  defaultChecked,
  onChange,
  className,
}: SelectionListItemProps) {
  const ctx = useContext(SelectionListContext);
  if (!ctx) {
    throw new Error('SelectionListItem은 SelectionList 안에서만 사용할 수 있습니다.');
  }

  const { type, name } = ctx;
  const reactId = useId();
  const controlId = `sli-${reactId.replace(/:/g, '')}`;

  const badgeClass = badge ? BADGE_COLOR_CLASS[badgeColor] : '';
  const showThumbSlot = Boolean(thumbnail) || thumbnailPlaceholder;
  const itemClassName = clsx(styles.item, checked && styles.item_checked, disabled && styles.item_disabled, className);

  return (
    <li
      className={itemClassName}
    >
      <label htmlFor={controlId} className={styles.item_inner}>
        {showThumbSlot ? (
          <span className={styles.thumbnail_wrap}>
            {thumbnail ? (
              <img src={thumbnail} alt="" className={styles.thumbnail} />
            ) : (
              <span className={styles.thumbnail_placeholder} aria-hidden />
            )}
          </span>
        ) : null}
        <span className={styles.body}>
          <span className={styles.label_row}>
            <span className={styles.label}>{label}</span>
            {tags?.map((tag, i) => (
              <span key={`${tag}-${i}`} className={`${styles.badge} ${styles.badge_gray}`}>
                {tag}
              </span>
            ))}
            {badge ? (
              <span className={clsx(styles.badge, badgeClass)}>{badge}</span>
            ) : null}
          </span>
          {subLabel ? <span className={styles.sub_label}>{subLabel}</span> : null}
        </span>
        {type === 'checkbox' ? (
          <Checkbox
            embedded
            id={controlId}
            name={name}
            value={value}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={(nextChecked) => emitCheckboxListChange(onChange, name, value, nextChecked)}
            size="md"
            className={styles.control_slot}
            // TODO :: embedded 모드에선 caption 보이지 않음. 플레이스홀더 문구로 보여 주석 처리 — 확인 후 삭제
            // caption="아래에 쓰이는 부가설명 텍스트."
          />
        ) : (
          <RadioButton
            embedded
            id={controlId}
            name={name}
            value={value}
            checked={checked}
            defaultChecked={defaultChecked}
            disabled={disabled}
            onChange={onChange}
            size="md"
            className={styles.control_slot}
          />
        )}
      </label>
    </li>
  );
}
